import {
  Button,
  Flex,
  Popover,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Textarea,
  useBoolean,
} from '@chakra-ui/react';
import expandViewImg from 'assets/images/interactionTypes/expand_view.png';
import labelImg from 'assets/images/interactionTypes/tooltip.png';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Interaction, InteractionType, InteractionsMap, TooltipInteraction } from 'types/interactiveWidget.types';

import { MAX_TOOLTIPS_CHARACTERS } from './InteractiveMenu.config';
import { OptionWrapper } from './common/OptionWrapper';

interface InteractionOptionProps {
  interactions?: InteractionsMap;
  updateInteraction?: (type: InteractionType, value: Interaction | null) => void;
}

export const ExpandInteractionOption = ({ interactions, updateInteraction }: InteractionOptionProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'interactive' });

  const isActive = !!interactions?.[InteractionType.Expandable];
  const label = t('expandable.label');

  const onClick = useCallback(() => {
    if (typeof updateInteraction !== 'function') return;

    if (isActive) {
      updateInteraction(InteractionType.Expandable, null);
    } else {
      updateInteraction(InteractionType.Expandable, { type: InteractionType.Expandable });
    }
  }, [isActive, updateInteraction]);

  return (
    <OptionWrapper
      imageSrc={expandViewImg}
      label={label}
      testId='interactive-option-expand'
      isActive={isActive}
      onClick={onClick}
    />
  );
};

export const LabelInteractionOption = ({ interactions, updateInteraction }: InteractionOptionProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'interactive' });

  const [isPopoverOpen, setIsPopoverOpen] = useBoolean(false);
  const tooltipInteraction = interactions?.[InteractionType.Tooltip] as TooltipInteraction;
  const isInteractionActive = !!tooltipInteraction && !!tooltipInteraction.data?.content;

  const [labelText, setLabelText] = useState(tooltipInteraction?.data?.content || '');

  const onChangeLabel = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setLabelText((e.target as HTMLTextAreaElement)?.value || '');
  }, []);

  const onSaveChanges = () => {
    setIsPopoverOpen.off();

    updateInteraction?.(InteractionType.Tooltip, {
      type: InteractionType.Tooltip,
      data: { content: labelText || '' },
    });
  };

  const onRemoveInteraction = () => {
    setIsPopoverOpen.off();
    updateInteraction?.(InteractionType.Tooltip, null);
  };

  // Sync label text with actual interaction data
  useEffect(() => {
    setLabelText(tooltipInteraction?.data?.content || '');
  }, [tooltipInteraction?.data?.content]);

  const label = t('tooltip.label');
  const removeLabel = t('tooltip.removeBtn');
  const saveLabel = t('tooltip.saveBtn');
  const placeholder = t('tooltip.placeholder');

  return (
    <Popover
      isLazy
      placement='right'
      isOpen={isPopoverOpen}
      onOpen={setIsPopoverOpen.on}
      onClose={setIsPopoverOpen.off}
      flip={false}
      preventOverflow={false}
    >
      <PopoverTrigger>
        <OptionWrapper
          isActive={isInteractionActive || isPopoverOpen}
          testId='interactive-option-label'
          imageSrc={labelImg}
          label={label}
        />
      </PopoverTrigger>
      <PopoverContent p={4} h='fit-content'>
        <Flex flexDir='column' gap={2}>
          <Flex justifyContent='flex-end' fontSize='xs' color='font.400'>
            {labelText.length} / {MAX_TOOLTIPS_CHARACTERS}
          </Flex>
          <Textarea
            value={labelText}
            onChange={onChangeLabel}
            data-testid='interactive-label-text-input'
            placeholder={placeholder}
            size='sm'
            maxLength={MAX_TOOLTIPS_CHARACTERS}
          />
          <PopoverFooter p={0} border='none'>
            <Flex justifyContent='flex-end' gap={2}>
              <Button variant='ghost' size='sm' onClick={onRemoveInteraction} data-testid='remove-interaction-btn'>
                {removeLabel}
              </Button>
              <Button variant='action' size='sm' onClick={onSaveChanges} data-testid='save-interaction-btn'>
                {saveLabel}
              </Button>
            </Flex>
          </PopoverFooter>
        </Flex>
      </PopoverContent>
    </Popover>
  );
};
